<template>
  <div class="field">
    <label class="label" v-if="!initialModal">{{ $resx("FileInput.Label") }}</label>
    <div class="field has-addons" v-if="!initialModal">
      <div class="control is-expanded has-icons-left" @click.prevent="showModal">
        <span class="icon is-small is-left">
          <font-awesome-icon :icon="icons.image" />
        </span>
        <div class="input-div" :title="inputValue">{{ inputValue }}</div>
      </div>
      <div class="control">
      <a class="button is-default" :class="{'is-static': !value || value === ''}" @click.prevent="lightboxSrc = $store.state.imgBase + getFilenameFromUrl(inputValue, imageProfiles[profile].maxWidth === 0).fullUrl">
        <font-awesome-icon :icon="icons.preview" />
      </a>
      </div>
      <div class="control">
        <a class="button is-danger" :class="{'is-static': !value || value === ''}" @click.prevent="deleteImage">
          <font-awesome-icon :icon="icons.delete" />
        </a>
      </div>
    </div>
    <via-modal :closeOnBackdrop="true" :image="lightboxSrc" :toBody="true" @close="lightboxSrc = ''"></via-modal>
    <via-modal :toBody="true" :active.sync="show" size="lg" :closeOnBackdrop="false" :autoClose="false" @close="resetImage">
      <template slot="body">
        <div class="columns">
          <div class="column is-12">
            <div class="columns is-multiline">
              <div class="field column is-4">
                <div class="control">
                  <div class="select">
                    <select v-model="selectedProfile">
                      <option v-for="(imageProfile, idx) in filteredProfiles" :value="imageProfile" :key="idx">{{ imageProfile | upperfirst }}</option>
                    </select>
                  </div>
                </div>
              </div>
              <div class="field column is-4">
                <div class="control">
                  <div class="select">
                    <select v-model="selectedSize" :disabled="imageProfiles[selectedProfile].imageWidth.length < 2 || name === ''">
                      <option v-for="(width, idx) in imageProfiles[selectedProfile].imageWidth" :value="idx" :key="idx">{{ $resx("FileInput.Sizes." + sizes[idx]) + ' ' + renderResultSize(idx) }}</option>
                    </select>
                  </div>
                </div>
              </div>
              <div class="field column is-4">
                <div class="control">
                  <div class="select">
                    <select v-model="inputRatio" :disabled="imageProfiles[selectedProfile].ratio.length < 2 || name === ''">
                      <option v-for="(ratio, idx) in imageProfiles[selectedProfile].ratio" :key="idx" :value="ratio.join('by')">{{ratio.join(':')}}</option>
                    </select>
                  </div>
                </div>
              </div>
              <div class="field column is-4">
                <div class="control">
                  <div class="field has-addons">
                    <p class="control">
                      <button class="button" :disabled="!imageProfiles[selectedProfile].align" :class="{'is-success': align === 'left' }" @click.prevent="align = 'left'">
                        <span>{{ $resx("Align.Left") }}</span>
                      </button>
                    </p>
                    <p class="control">
                      <button class="button" :disabled="!imageProfiles[selectedProfile].align" :class="{'is-success': align === 'center' }" @click.prevent="align = 'center'">
                        <span>{{ $resx("Align.Center") }}</span>
                      </button>
                    </p>
                    <p class="control">
                      <button class="button" :disabled="!imageProfiles[selectedProfile].align" :class="{'is-success': align === 'right' }" @click.prevent="align = 'right'">
                        <span>{{ $resx("Align.Right") }}</span>
                      </button>
                    </p>
                  </div>
                </div>
              </div>
              <div class="field column is-4">
                <div class="control">
                  <input class="input" type="text" v-model="alt" :placeholder="$resx('FileInput.Alt')" :disabled="!imageProfiles[selectedProfile].alt" />
                </div>
              </div>
              <div class="field column is-4">
                <div class="control">
                  <input class="input" type="text" v-model="caption" :placeholder="$resx('FileInput.Caption')" :disabled="!imageProfiles[selectedProfile].caption" />
                </div>
              </div>
              <div class="field column is-4">
                <div class="image-info">
                  <div class="columns is-multiline is-gapless">
                    <div class="column is-12">{{ $resx("FileInput.Original") }}</div>
                    <div class="column is-12 label">{{ $resx("FileInput.Name") }}</div>
                    <div class="column is-12">{{ name === '' ? '-' : name }}</div>
                    <div class="column is-6 label">{{ $resx("FileInput.Width") }}</div>
                    <div class="column is-6 label">{{ $resx("FileInput.Height") }}</div>
                    <div class="column is-6">{{ original[0] }}px</div>
                    <div class="column is-6">{{ original[1] }}px</div>
                    <div class="column is-12 label">{{ $resx("FileInput.Size") }}</div>
                    <div class="column is-12">{{ printHumanSize(file) }}</div>
                  </div>
                  <div class="columns is-multiline is-gapless">
                    <div class="column is-12">{{ $resx("FileInput.Result") }}</div>
                    <div class="column is-6 label">{{ $resx("FileInput.Width") }}</div>
                    <div class="column is-6 label">{{ $resx("FileInput.Height") }}</div>
                    <div class="column is-6">{{ resultWH[0] }}px</div>
                    <div class="column is-6">{{ resultWH[1] }}px</div>
                    <div class="column is-12 label">{{ $resx("FileInput.Expected") }}</div>
                    <div class="column is-12">{{ printHumanSize(file, (selectedProfile === 'icon' ? false : ((100 / (original[0] / resultWH[0])) + (100 / (original[1] / resultWH[1])) / 2))) }}</div>
                    <div class="column is-12 label" v-if="imageProfiles[selectedProfile].maxWidth !== 0">{{ $resx("FileInput.MaxWidth") }}</div>
                    <div class="column is-12" v-if="imageProfiles[selectedProfile].maxWidth !== 0">{{ imageProfiles[selectedProfile].maxWidth + 'px' }}</div>
                  </div>
                </div>
              </div>
              <div class="column is-8">
                <div class="image-upload" ref="imageUpload">
                  <label class="uploader" :class="{'dragover': dragover}" v-if="name === ''" @drop.prevent="onDropHandler" @dragover.prevent="dragover = true" @dragleave.prevent="dragover = false">
                    <div>
                      <font-awesome-icon :icon="icons.upload" size="3x" />
                      <p>{{ $resx("FileInput.ClickOrDrop") }}</p>
                    </div>
                    <input ref="file" type="file" :accept="acceptTypes" @change="onFileChange" name="image" />
                  </label>
                  <div v-else>
                    <div :style="{backgroundImage: 'url(' + src + ')'}" class="cropper-background"></div>
                    <cropper
                      ref="cropper"
                      classname="cropper"
                      :src="src"
                      :stencil-props="stencil"
                      :canvas="false"
                      :debounce="200"
                      :defaultPosition="defaultPosition"
                      :defaultSize="defaultSize"
                      :restrictions="restriction"
                      @change="cropChange"
                    ></cropper>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="uploading" v-if="uploading">
            <p>{{ $resx("FileInput.Uploading") + ' ' + Math.round(progress) }}%</p>
            <progress class="progress is-small" :value="progress" max="100">{{ Math.round(progress) }}%</progress>
          </div>
        </div>
      </template>
      <template slot="footer">
        <button class="button is-pulled-left" @click.prevent="hideModal">{{ $resx("Buttons.Cancel") }}</button>
        <button class="button is-pulled-right is-success" @click.prevent="uploadImage" :disabled="uploading || !file ? 'disabled' : false">{{ $resx("Buttons.Upload") }}</button>
      </template>
    </via-modal>
  </div>
</template>

<script>

import { faUpload, faImage, faPencil, faTrashAlt, faEye } from '@fortawesome/pro-regular-svg-icons'
import { confirm } from '@/mixins/confirm.js'
import { getFilenameFromUrl } from '@/mixins/getFilenameFromUrl.js'
import viaModal from '@/components/Modal.vue'
import { Cropper } from 'vue-advanced-cropper'

export default {
  name: 'fileInput',
  mixins: [confirm, getFilenameFromUrl],
  data () {
    return {
      oldValue: '',
      lightboxSrc: '',
      src: '',
      show: this.control,
      procent: '0',
      alt: '',
      caption: '',
      align: 'left',
      ratio: [],
      uploading: false,
      file: false,
      maxWidth: 4000,
      sizes: ['Small', 'Medium', 'Large', 'ExtraLarge'],
      selectedProfile: this.profile,
      selectedSize: 0,
      cropperDisabled: false,
      overlayColor: process.env.VUE_APP_OVERLAYCOLOR,
      image: {
        name: '',
        original: [0, 0],
        selected: [0, 0, 0, 0]
      },
      icons: {
        upload: faUpload,
        edit: faPencil,
        image: faImage,
        delete: faTrashAlt,
        preview: faEye
      },
      imageProfiles: {
        meta: {
          ratio: [[2, 1]],
          defaultRatio: [2, 1],
          imageWidth: [1400],
          accept: ['jpeg', 'png', 'gif'],
          maxWidth: 0,
          makeGrayscale: false,
          colorOverlay: false,
          alt: false,
          align: false,
          caption: false,
          enlargeable: false
        },
        header: {
          ratio: [[2, 1]],
          defaultRatio: [2, 1],
          imageWidth: [100],
          accept: ['jpeg', 'png', 'gif'],
          maxWidth: 4500,
          makeGrayscale: false,
          colorOverlay: false,
          alt: false,
          align: false,
          caption: false,
          enlargeable: false
        },
        theme: {
          ratio: [[2, 1]],
          defaultRatio: [2, 1],
          imageWidth: [500],
          accept: ['jpeg', 'png', 'gif'],
          maxWidth: 4500,
          makeGrayscale: true,
          colorOverlay: true,
          alt: false,
          align: false,
          caption: false,
          enlargeable: false
        },
        inline: {
          ratio: [[1, 1]],
          defaultRatio: [1, 1],
          imageWidth: [15],
          accept: ['jpeg', 'png', 'gif'],
          maxWidth: 0,
          makeGrayscale: false,
          colorOverlay: false,
          alt: true,
          align: false,
          caption: false,
          enlargeable: false
        },
        fullwidth: {
          ratio: [[5, 4], [4, 3], [3, 2], [5, 3], [16, 9], [2, 1], [3, 1]],
          defaultRatio: [2, 1],
          imageWidth: [1400],
          accept: ['jpeg', 'png', 'gif'],
          maxWidth: 0,
          makeGrayscale: false,
          colorOverlay: false,
          alt: true,
          align: false,
          caption: true,
          enlargeable: true
        },
        image: {
          ratio: [[1, 1], [5, 4], [4, 3], [3, 2], [5, 3], [16, 9], [2, 1], [3, 1], [4, 5], [3, 4], [2, 3], [3, 5], [9, 16], [1, 2], [1, 3]],
          defaultRatio: [16, 9],
          imageWidth: [200, 500, 800],
          accept: ['jpeg', 'png', 'gif'],
          maxWidth: 2600,
          makeGrayscale: false,
          colorOverlay: false,
          alt: true,
          align: true,
          caption: true,
          enlargeable: true
        },
        icon: {
          ratio: [[1, 1]],
          defaultRatio: [1, 1],
          imageWidth: [15, 30, 50, 80],
          accept: ['svg+xml'],
          maxWidth: 0,
          makeGrayscale: false,
          colorOverlay: false,
          alt: true,
          align: false,
          caption: false,
          enlargeable: false
        },
        avatar: {
          ratio: [[1, 1]],
          defaultRatio: [1, 1],
          imageWidth: [200],
          accept: ['jpeg', 'png', 'gif'],
          maxWidth: 0,
          makeGrayscale: false,
          colorOverlay: false,
          alt: false,
          align: false,
          caption: false,
          enlargeable: false
        },
        partner: {
          ratio: [[2, 1]],
          defaultRatio: [2, 1],
          imageWidth: [300],
          accept: ['jpeg', 'png', 'gif'],
          maxWidth: 0,
          makeGrayscale: false,
          colorOverlay: false,
          alt: false,
          align: false,
          caption: false,
          enlargeable: false
        }
      },
      dragover: false
    }
  },
  props: {
    value: String,
    profile: {
      type: String,
      default: 'header'
    },
    initialModal: {
      type: Boolean,
      default: false
    },
    control: {
      type: Boolean,
      default: false
    },
    disabledProfiles: {
      type: Array,
      default: () => []
    }
  },
  components: {
    viaModal,
    Cropper
  },
  computed: {
    filteredProfiles () {
      var self = this
      var tmp = []
      Object.keys(self.imageProfiles).forEach(key => {
        if (!self.disabledProfiles.includes(key)) { tmp.push(key) }
      })
      return tmp
    },
    acceptTypes () {
      var self = this
      var tmp = []
      self.imageProfiles[self.selectedProfile].accept.forEach((item) => {
        tmp.push('image/' + item)
      })
      return tmp.join(', ')
    },
    inputValue () {
      var self = this
      return !self.value || self.value === '' ? self.$resx('FileInput.AddImage') : self.value
    },
    selected: {
      get () {
        return this.image.selected
      },
      set (value) {
        this.image.selected = value
      }
    },
    resultWH () {
      var self = this
      var factor = self.ratio[1] / self.ratio[0]
      var width = self.file ? self.imageProfiles[self.selectedProfile].imageWidth[self.selectedSize] : 0
      var height = self.file ? width * (factor || 1) : 0
      return [Math.round(width), Math.round(height)]
    },
    selectedWH () {
      return [Math.round(this.selected[0]), Math.round(this.selected[1]), Math.round(this.selected[2]), Math.round(this.selected[3])]
    },
    original: {
      get () {
        return [Math.round(this.image.original[0]), Math.round(this.image.original[1])]
      },
      set (value) {
        this.image.original = value
      }
    },
    name: {
      get () {
        return this.image.name
      },
      set (value) {
        this.image.name = value
      }
    },
    progress: {
      get () {
        return this.procent
      },
      set (value) {
        this.procent = value
      }
    },
    inputRatio: {
      get () {
        var self = this
        if (self.ratio.length === 0) {
          return self.imageProfiles[self.selectedProfile].defaultRatio.join('by')
        } else {
          return self.ratio.join('by')
        }
      },
      set (value) {
        var self = this
        var newValue = value.split('by').map(el => {
          var n = Number(el)
          return n === 0 ? n : n || el
        })
        self.ratio = newValue
      }
    },
    stencil () {
      var self = this
      var options = {}
      if (self.cropperDisabled) {
        options.handlers = {}
        options.movable = false
        options.scalable = false
      } else {
        options.handlers = {
          eastNorth: true,
          north: false,
          westNorth: true,
          west: false,
          westSouth: true,
          south: false,
          eastSouth: true,
          east: false
        }
        options.movable = true
        options.scalable = true

        if (self.ratio) {
          options.aspectRatio = self.ratio[0] / self.ratio[1]
        }
      }
      return options
    }
  },
  methods: {
    printHumanSize (file, factor) {
      factor = factor || false
      var size = (file && file.size > 0 ? (factor ? file.size * (factor / 100) : file.size) : false)
      var i = !size || size === 0 ? 0 : Math.floor(Math.log(size) / Math.log(1024))
      var result = i === 0 && !size ? '-' : (size / Math.pow(1024, i)).toFixed((i < 2 ? 0 : 2)) * 1 + ' ' + ['B', 'kB', 'MB', 'GB', 'TB'][i]
      return result
    },
    renderResultSize: function (idx) {
      var self = this
      var factor = self.ratio[1] / self.ratio[0]
      var width = self.imageProfiles[self.selectedProfile].imageWidth[idx]
      return Math.round(width) + 'x' + Math.round(width * (factor || 0))
    },
    showModal: function () {
      this.show = true
    },
    resetImage: function (keepProfile) {
      var self = this
      keepProfile = keepProfile || false
      self.name = ''
      self.src = ''
      self.file = false
      self.uploading = false
      self.selected = [0, 0, 0, 0]
      self.original = [0, 0]
      self.alt = ''
      self.caption = ''
      self.align = 'left'
      self.ratio = self.imageProfiles[self.selectedProfile].defaultRatio
      self.selectedSize = 0
      if (!keepProfile) {
        self.selectedProfile = self.profile
      }
    },
    defaultSize ({ minWidth, minHeight, maxWidth, maxHeight, imageWidth, imageHeight, aspectRatio }) {
      var self = this

      var newHeight, newWidth
      if (self.selectedProfile === 'icon') {
        newHeight = imageHeight
        newWidth = imageWidth
      } else {
        var areaWidth = imageWidth
        var areaHeight = imageHeight

        const ratio = imageWidth > imageHeight ? aspectRatio.maximum : aspectRatio.minimum

        if (imageWidth / imageHeight > aspectRatio.maximum || imageWidth / imageHeight < aspectRatio.minimum) {
          if (ratio <= 1) {
            areaHeight = imageHeight
            areaWidth = areaHeight * aspectRatio.maximum
          } else {
            areaWidth = imageWidth
            areaHeight = areaWidth / aspectRatio.minimum
          }
        }

        newHeight = Math.min(maxHeight, Math.max(minHeight, areaHeight * 0.8))
        newWidth = Math.min(maxWidth, Math.max(minWidth, areaWidth * 0.8))
      }
      return {
        height: newHeight,
        width: newWidth
      }
    },
    defaultPosition ({ imageWidth, imageHeight, stencilWidth, stencilHeight }) {
      return {
        left: (self.selectedProfile === 'icon' ? 0 : imageWidth / 2 - stencilWidth / 2),
        top: (self.selectedProfile === 'icon' ? 0 : imageHeight / 2 - stencilHeight / 2)
      }
    },
    restriction: function ({ minWidth, minHeight, maxWidth, maxHeight, imageWidth, imageHeight }) {
      var self = this
      var factor = self.ratio[1] / self.ratio[0]
      var height = self.maxWidth * (factor || 1)

      return {
        maxWidth: self.maxWidth,
        maxHeight: height,
        minWidth: Math.abs(self.imageProfiles[self.selectedProfile].imageWidth[self.selectedSize]),
        minHeight: Math.abs(self.imageProfiles[self.selectedProfile].imageWidth[self.selectedSize] * (factor || 1))
      }
    },
    deleteImage: function () {
      var self = this
      self.confirm({
        message: self.$resx('Global.AreYouSure'),
        buttons: [
          {
            name: self.$resx('Buttons.Cancel'),
            class: 'is-pulled-left'
          },
          {
            name: self.$resx('Buttons.Yes'),
            class: 'is-pulled-right is-danger',
            action: function () {
              var image = {
                url: '',
                alt: '',
                caption: '',
                align: 'left',
                ratio: ''
              }
              self.$emit('complete', image)
              self.show = false
              self.resetImage()
            }
          }
        ]
      })
    },
    hideModal: function () {
      var self = this
      self.show = false
      self.resetImage()
      self.$emit('canceled')
    },
    uploadImage: function () {
      var self = this
      self.uploading = true

      if (self.file) {
        var thumbWidth = self.imageProfiles[self.selectedProfile].imageWidth[self.selectedSize] < self.selected[2] ? self.imageProfiles[self.selectedProfile].imageWidth[self.selectedSize] : false

        if (!self.initialModal && self.oldValue !== '' && self.oldValue !== self.value) {
          var file = self.getFilenameFromUrl(self.value)
          self.$store.dispatch('deleteFile', file)
        }

        var formData = new FormData()
        formData.append('FormFile', self.file)

        if (self.selectedProfile !== 'icon') {
          formData.set('ResizeDetails.CropX', Math.floor(self.selected[0]))
          formData.set('ResizeDetails.CropY', Math.floor(self.selected[1]))
          formData.set('ResizeDetails.Width', Math.floor(self.selected[2]))
          formData.set('ResizeDetails.Height', Math.floor(self.selected[3]))
          if (self.imageProfiles[self.selectedProfile].maxWidth !== 0) {
            formData.set('ResizeDetails.MaxWidth', self.imageProfiles[self.selectedProfile].maxWidth)
          }
          formData.set('ResizeDetails.ThumbWidth', (thumbWidth || Math.floor(self.selected[2])))
          formData.set('ResizeDetails.MakeGrayscale', false)
          formData.set('ResizeDetails.ColorOverlay', '')
        }

        var progressCount = 1 + +self.imageProfiles[self.selectedProfile].makeGrayscale + +self.imageProfiles[self.selectedProfile].colorOverlay
        var progressWidth = 100 / progressCount// self.imageProfiles[self.selectedProfile].makeGrayscale ? 50 : 100

        self.$http({
          method: 'POST',
          url: '/general/file',
          api: 'website',
          name: process.env.VUE_APP_NAME + '_' + self.$store.state.language,
          data: formData,
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const totalLength = progressEvent.lengthComputable ? progressEvent.total : progressEvent.target.getResponseHeader('content-length') || progressEvent.target.getResponseHeader('x-decompressed-content-length')
            if (totalLength !== null) {
              var procent = Math.round((progressEvent.loaded * progressWidth) / totalLength)
              self.progress = procent
            }
          }
        }).then(function (response) {
          new Promise(function (resolve) {
            if (self.imageProfiles[self.selectedProfile].makeGrayscale) {
              formData.set('ResizeDetails.MakeGrayscale', true)
              self.$http({
                method: 'POST',
                url: '/general/file',
                api: 'website',
                name: process.env.VUE_APP_NAME + '_' + self.$store.state.language,
                data: formData,
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (progressEvent) => {
                  const totalLength = progressEvent.lengthComputable ? progressEvent.total : progressEvent.target.getResponseHeader('content-length') || progressEvent.target.getResponseHeader('x-decompressed-content-length')
                  if (totalLength !== null) {
                    var procent = Math.round((progressEvent.loaded * progressWidth) / totalLength)
                    self.progress = progressWidth + procent
                  }
                }
              }).then(function (grey) {
                resolve(grey.data.FilePath)
              }).catch(function (err) {
                self.$store.dispatch('setSnack', {
                  message: err.message
                })
                resolve(false)
              })
            } else {
              resolve(false)
            }
          }).then(function (gray) {
            // if (response.status === 201) {
            //   var image = {
            //     url: response.data.FilePath,
            //     alt: self.imageProfiles[self.selectedProfile].alt ? self.alt : false,
            //     caption: self.imageProfiles[self.selectedProfile].caption ? self.caption : false,
            //     align: self.imageProfiles[self.selectedProfile].align ? self.align : false,
            //     ratio: self.inputRatio,
            //     profile: self.selectedProfile,
            //     thumbWidth: thumbWidth,
            //     grayscale: gray,
            //     enlargeable: self.imageProfiles[self.selectedProfile].enlargeable
            //   }
            //   self.$emit('complete', image)
            //   self.show = false
            //   self.resetImage()
            // } else {
            //   self.hideModal()
            // }

            new Promise(function (resolve) {
              if (self.imageProfiles[self.selectedProfile].colorOverlay && self.overlayColor && self.overlayColor !== '') {
                formData.set('ResizeDetails.ColorOverlay', self.overlayColor)
                self.$http({
                  method: 'POST',
                  url: '/general/file',
                  api: 'website',
                  name: process.env.VUE_APP_NAME + '_' + self.$store.state.language,
                  data: formData,
                  headers: { 'Content-Type': 'multipart/form-data' },
                  onUploadProgress: (progressEvent) => {
                    const totalLength = progressEvent.lengthComputable ? progressEvent.total : progressEvent.target.getResponseHeader('content-length') || progressEvent.target.getResponseHeader('x-decompressed-content-length')
                    if (totalLength !== null) {
                      var procent = Math.round((progressEvent.loaded * progressWidth) / totalLength)
                      self.progress = (100 - progressWidth) + procent
                    }
                  }
                }).then(function (over) {
                  resolve(over.data.FilePath)
                }).catch(function (err) {
                  self.$store.dispatch('setSnack', {
                    message: err.message
                  })
                  resolve('')
                })
              } else {
                resolve('')
              }
            }).then(function (overlay) {
              if (response.status === 201) {
                var image = {
                  url: response.data.FilePath,
                  alt: self.imageProfiles[self.selectedProfile].alt ? self.alt : false,
                  caption: self.imageProfiles[self.selectedProfile].caption ? self.caption : false,
                  align: self.imageProfiles[self.selectedProfile].align ? self.align : false,
                  ratio: self.inputRatio,
                  profile: self.selectedProfile,
                  thumbWidth: thumbWidth,
                  grayscale: gray,
                  overlay: overlay,
                  enlargeable: self.imageProfiles[self.selectedProfile].enlargeable
                }
                self.$emit('complete', image)
                self.show = false
                self.resetImage()
              } else {
                self.hideModal()
              }
            })
          })
        }).catch(function (err) {
          self.$store.dispatch('setSnack', {
            message: err.message
          })
          self.hideModal()
        })
      }
    },
    readFile: function (file) {
      var self = this
      var reader = new FileReader()
      reader.onload = function (e) {
        var image = new Image()
        image.src = e.target.result
        image.onload = function () {
          var factor = self.ratio[1] / self.ratio[0]
          var min = {
            width: self.imageProfiles[self.selectedProfile].imageWidth[self.selectedSize],
            height: self.imageProfiles[self.selectedProfile].imageWidth[self.selectedSize] * (factor || 1)
          }
          if (this.width < min.width || this.height < min.height) {
            self.$refs.file.value = ''
            alert(self.$resx('FileInput.ImageToSmall'))
          } else {
            self.file = file
            self.name = file.name
            self.original = [this.width, this.height]
            self.src = e.target.result
          }
        }
      }
      reader.readAsDataURL(file)
    },
    onDropHandler: function (e) {
      var self = this
      var file = false
      self.dragover = false
      if (e.dataTransfer.items) {
        var firstFileIdx = -1
        for (var i = 0; i < e.dataTransfer.items.length; i++) {
          if (e.dataTransfer.items[i].kind === 'file') {
            firstFileIdx = i
          }
        }
        file = e.dataTransfer.items[firstFileIdx].getAsFile()
      } else {
        file = e.dataTransfer.files[0].name
      }
      self.readFile(file)
    },
    onFileChange: function (e) {
      var self = this
      var files = e.target.files || e.dataTransfer.files
      if (files.length > 0) {
        self.readFile(files[0])
      }
    },
    cropChange: function ({ coordinates }) {
      this.selected = [coordinates.left, coordinates.top, coordinates.width, coordinates.height]
    }
  },
  beforeMount () {
    var self = this
    self.ratio = self.imageProfiles[self.selectedProfile].defaultRatio
  },
  created () {
    var self = this
    self.oldValue = (self.value && self.value !== '' ? self.value.slice() : '')
  },
  watch: {
    control (value) {
      this.show = value
    },
    selectedProfile (value) {
      var self = this
      self.cropperDisabled = value === 'icon'
      var type = self.file ? self.file.type.replace('image/', '') : false
      if (type && !self.imageProfiles[value].accept.includes(type)) {
        self.resetImage(true)
      } else {
        self.ratio = self.imageProfiles[value].defaultRatio
        self.selectedSize = 0
      }
    },
    selectedSize (idx) {
      var self = this
      if (self.imageProfiles[self.selectedProfile].imageWidth[idx] > self.selected[2]) {
        self.$refs.cropper.resetCoordinates()
      }
    }
  }
}

</script>

<style lang="scss" scoped>

.field{
  &.column{
    margin-bottom: 0;
    .select, select{
      width: 100%;
    }
    p.control{
      flex: 1;
      button{
        width: 100%;
        padding-left: 0;
        padding-right: 0;
      }
    }
    &.reset{
      padding-top: 0;
      padding-left: 14px;
      padding-right: 14px;
      line-height: 14px;
      small, a{
        color: $danger;
        line-height: 10px;
      }
    }
    .image-info{
      >div{
        margin-bottom: 10px;
        padding-bottom: 10px;
        border: 1px solid $grey-lighter;
        padding: 0px 0px 10px 0px;
        border-radius: 4px;
        overflow: hidden;
        *{
          font-size: 14px;
        }
        div{
          text-overflow: ellipsis;
          overflow: hidden;
          white-space: nowrap;
          padding: 0px 10px !important;
          &:not(.label){
            padding: 0px 12px !important;
            margin-top: -4px;
          }
          &.is-12:first-of-type{
            font-size: 12px;
            // font-weight: 700;
            margin-top: 0;
            line-height: 14px;
            padding: 4px 6px 4px !important;
            text-align: left;
            border-bottom: 1px solid $grey-lighter;
            background-color: $grey-bg;
          }
          &.label{
            font-size: 11px;
            line-height: 20px;
            margin-top: 4px;
          }
          >.preview{
            margin: 10px 0;
            height: 112px;
            background-color: $grey-bg;
            display: flex;
            justify-content: center;
            flex-direction: column;
            text-align: center;
            align-items: center;

            img{
              max-height: 112px;
              max-width: 100%;
              height: auto;
              width: auto;
            }
          }
        }
      }
    }
  }
  .control:first-child{
    min-width: 0px;
    .input-div{
      flex: 1;
      cursor: pointer;
      -webkit-box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.1);
      box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.1);
      background-color: #fcfdfd;
      border-radius: 4px 0px 0px 4px;
      align-items: center;
      border: 1px solid #dbdbdb;
      height: 2.25em;
      justify-content: flex-start;
      line-height: 1.5;
      padding-bottom: calc(0.375em - 1px);
      padding-left: 2.25em;
      padding-right: calc(0.625em - 1px);
      padding-top: calc(0.375em - 1px);
      position: relative;
      vertical-align: top;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
  &.is-horizontal{
    margin-bottom: 0;

    .is-pulled-right{
      margin-left: auto;
    }
  }
}
.image-upload{
  height: 360px;
  background-color: $grey-bg;
  position: relative;
  overflow: hidden;
  border-radius: 4px;
  .uploader{
    position: absolute;
    height: 100%;
    width: 100%;
    top: 0;
    left: 0;
    text-align: center;
    padding-top: 80px;
    color: $grey-lighter;
    border: 1px dashed $grey;
    border-radius: 4px;
    &:hover{
      cursor: pointer;
    }

    input{
      position: absolute;
      left: 0;
      opacity: 0;
      top: 0;
      bottom: 0;
      width: 100%;
      z-index: -1;
    }
    p{
      margin-top: 20px;
      color: $grey-light;
    }
    &.dragover{
      border: 1px solid $grey;
      p{
        color: $grey-dark !important;
      }
    }
  }
  .cropper-background {
    position: absolute;
    width: 100%;
    height: 100%;
    background-size: cover;
    background-position: 50%;
    filter: blur(5px);
    opacity: 0.25;
  }
  /deep/.cropper{
    height: 360px;
    // background-color: $gray-bg;
    border: 1px solid $grey-lighter;
    border-radius: 4px;

    .vue-advanced-cropper__background{
      background: rgba(0,0,0,0.8);
    }
    .vue-simple-line{
      border-style: dashed;
    }
    .vue-handler-wrapper{
      .vue-square-handler{
        background: $white;
      }
    }
  }
}
.uploading{
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: $white;
  opacity: 0.96;
  padding: 60px 40px;
  text-align: center;
  z-index: 100;
  p{
    margin-bottom: 10px;
  }
  .progress::-webkit-progress-value {
    transition: width 0.5s ease;
  }
}
</style>
